import { ICalendarEventMinimumData } from '@/components/week-calendar/types/ICalendarEvent';
import { IInnerEventProps, WeekCalendar } from '@/components/week-calendar/week-calendar.view';
import usePureState from '@/hooks/usePureState';
import timezoneService from '@/services/timezone.service';
import { ICalendarEvent } from '@/types/calendar-event/ICalendarEvent';
import { Box, Text } from '@chakra-ui/react';
import { Temporal } from 'temporal-polyfill';
import { useCalendarWeekView } from './calendar-week-view.controller';
import EventDetailsModal from './components/event-details-modal/event-details.view';

export default function CalendarWeekView() {
	const {
		calendarEventsQuery,
		calendarQuery,
		from,
		isLoading,
		selectInterval,
		timeZone,
		to,
		handleRemoveEventClick,
		handleCalendarEventUpdated,
	} = useCalendarWeekView();

	const { state, dispatch } = usePureState({
		editEventId: '',
	});

	if (!calendarQuery.data) {
		return null;
	}

	return (
		<Box bg="gray.50">
			<EventDetailsModal
				calendar={calendarQuery.data}
				key={state.editEventId}
				isOpen={!!calendarEventsQuery.data?.items.find((e) => e.id === state.editEventId)}
				onClose={() => {
					dispatch({ editEventId: '' });
				}}
				onDelete={() => {
					handleRemoveEventClick(state.editEventId);
				}}
				event={calendarEventsQuery.data?.items.find((e) => e.id === state.editEventId) as ICalendarEvent}
				onSave={(event) => {
					handleCalendarEventUpdated(event);
				}}
			/>
			<WeekCalendar
				isLoading={isLoading}
				events={
					calendarEventsQuery.data?.items
						.filter((e) => e.interval)
						.map((i) => {
							return {
								...i,
								id: i.id,
								start: Temporal.Instant.from(i.interval!.inclusiveStart.dateTime).toZonedDateTimeISO(
									i.interval!.inclusiveStart.timeZone || timezoneService.getTimeZone()
								),
								end: Temporal.Instant.from(i.interval!.exclusiveEnd.dateTime).toZonedDateTimeISO(
									i.interval!.exclusiveEnd.timeZone || timezoneService.getTimeZone()
								),
							};
						}) ?? []
				}
				timeZone={timeZone}
				from={from}
				to={to}
				defaultActiveDay={from}
				onEventClick={(args) => {
					args.event.stopPropagation();
					dispatch({ editEventId: args.calendarEvent.id });
				}}
				onEventUpdated={(event) => {
					event.interval = {
						inclusiveStart: {
							dateTime: event.start.round({ smallestUnit: 'minute', roundingIncrement: 15 }).toInstant().toString(),
							timeZone: event.start.timeZoneId,
						},
						exclusiveEnd: {
							dateTime: event.end.round({ smallestUnit: 'minute', roundingIncrement: 15 }).toInstant().toString(),
							timeZone: event.end.timeZoneId,
						},
					};
					handleCalendarEventUpdated(event);
				}}
				onCalendarPointerMove={selectInterval.handleCalendarPointerMove}
				onCalendarPointerUp={selectInterval.handleCalendarPointerUp}
				onCalendarPointerDown={selectInterval.handleCalendarPointerDown}
				highlight={selectInterval.highlight}
				InnerEvent={InnerEvent}
			/>
		</Box>
	);
}

function InnerEvent(props: IInnerEventProps<ICalendarEventMinimumData & ICalendarEvent>) {
	return (
		<>
			<Box
				p={1}
				backgroundColor="white"
				w="full"
				overflow="hidden"
				borderWidth={1}
				borderLeftWidth={4}
				borderLeftColor="primary.500"
				borderRadius="sm"
				shadow="sm"
				onClick={(e) => {
					e.stopPropagation();
					if (props.metadata.onClick) {
						props.metadata.onClick({ event: e, calendarEvent: props.event });
					}
				}}
			>
				<Text fontWeight={600} color="primary.700" fontSize="xs">
					{props.event.summary}
				</Text>
				<Text fontSize="xs" color="primary.700">
					{props.event.start.toLocaleString('es', { hour: '2-digit', minute: '2-digit' })} -{' '}
					{props.event.end.toLocaleString('es', { hour: '2-digit', minute: '2-digit' })}
				</Text>
			</Box>
		</>
	);
}
